import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  trialImages,
  trialMosaicImageFallback,
  trialMosaicPhotoUrl,
} from './trial-media';
import { Shopfront } from './shopfront';
import { Crew } from './crew';

const PHOTO_TILE_COUNT = 22;

type Pin = {
  x: string;
  y: string;
  label: string;
  ringDelay: string;
  delay: string;
  labelDelay: string;
};

function plotPins(names: string[], zones: number[][], y0: number, y1: number, max: number, cols: number): Pin[] {
  const shown = names.slice(0, max);
  const per = zones.map(() => [] as Array<{ name: string; i: number }>);

  shown.forEach((name, i) => per[i % zones.length].push({ name, i }));

  return per.flatMap((list, z) => {
    const [x0, x1] = zones[z];
    const rows = Math.max(1, Math.ceil(list.length / cols));

    return list.map((item, k) => {
      const r = Math.floor(k / cols);
      const c = k % cols;
      const jx = (((item.i * 37) % 7) - 3) * 0.35;
      const jy = (((item.i * 53) % 9) - 4) * 0.45;
      const x = x0 + ((c + 0.5) / cols) * (x1 - x0) + (r % 2 ? 1 : -1) * (x1 - x0) * 0.07 + jx;
      const y = y0 + ((r + 0.5) / rows) * (y1 - y0) + jy;

      return {
        x: `${x.toFixed(2)}%`,
        y: `${y.toFixed(2)}%`,
        label: item.name,
        ringDelay: `${item.i * 0.27}s`,
        delay: `${(0.5 + item.i * 0.15).toFixed(2)}s`,
        labelDelay: `${(0.85 + item.i * 0.15).toFixed(2)}s`,
      };
    });
  });
}

function onColor(hex: string) {
  const value = hex.replace('#', '');

  if (value.length !== 6) return '#ffffff';

  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);

  return (0.299 * r + 0.587 * g + 0.114 * b) > 170 ? '#19214d' : '#ffffff';
}

type Props = {
  company: string;
  brand: string;
  accent: string;
  locations: string[];
  images: Array<string | null | undefined>;
  wide?: boolean;
};

export const SetupBackdrop = memo(({
  company, brand, accent, locations, images, wide,
}: Props) => {
  const { t } = useTranslation();
  const photos = useMemo(() => trialImages(images), [images]);
  const tiles = useMemo(
    () => Array.from({ length: PHOTO_TILE_COUNT }, (_, index) => {
      const img = trialMosaicPhotoUrl(photos, index);

      return {
        id: `photo-${index}-${img}`,
        img,
        bg: '#2b3157',
        span: index % 3 === 0 ? 2 : 1,
      };
    }),
    [photos],
  );
  const namedLocations = locations.filter(Boolean);
  const pins = useMemo(
    () => plotPins(namedLocations, wide ? [[3, 21], [79, 97]] : [[5, 29], [71, 95]], 22, 82, 12, 2),
    [namedLocations, wide],
  );
  const chats = [
    {
      name: t('authentication:trial_backdrop_chat_all', { name: company }),
      msg: t('authentication:trial_backdrop_chat_update'),
      color: brand,
      count: 3,
    },
    {
      name: namedLocations[0] || company,
      msg: t('authentication:trial_backdrop_chat_swap'),
      color: accent,
      count: 2,
    },
    {
      name: namedLocations[1] || company,
      msg: t('authentication:trial_backdrop_chat_today'),
      color: '#19214d',
      count: 0,
    },
    {
      name: company,
      msg: t('authentication:trial_backdrop_chat_weekly'),
      color: brand,
      count: 1,
    },
    {
      name: namedLocations[2] || company,
      msg: t('authentication:trial_backdrop_chat_keys'),
      color: accent,
      count: 0,
    },
  ];

  return (
    <div className="FreeTrial__Backdrop" aria-hidden="true">
      <div className="FreeTrial__Backdrop__Scene FreeTrial__Backdrop__Scene--map">
        <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="trial-bd-dots" width="22" height="22" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.1" fill="#c7d2fe" fillOpacity=".12" />
            </pattern>
            <pattern id="trial-bd-grid" width="92" height="92" patternUnits="userSpaceOnUse" patternTransform="rotate(-14)">
              <path d="M0 0H92M0 0V92" stroke="#c7d2fe" strokeOpacity=".06" strokeWidth="1.5" fill="none" />
            </pattern>
          </defs>
          <rect width="1440" height="900" fill="#121838" />
          <rect width="1440" height="900" fill="url(#trial-bd-grid)" />
          <ellipse cx="120" cy="300" rx="70" ry="46" fill="#7ea3ff" fillOpacity=".06" />
          <ellipse cx="1260" cy="330" rx="80" ry="50" fill="#7ea3ff" fillOpacity=".06" />
          <ellipse cx="1090" cy="760" rx="60" ry="40" fill="#7ea3ff" fillOpacity=".06" />
          <ellipse cx="330" cy="800" rx="70" ry="40" fill="#7ea3ff" fillOpacity=".06" />
          <path
            d="M-40 600C160 560 300 660 520 620C720 584 860 520 1060 560C1240 596 1340 660 1480 640"
            fill="none"
            stroke="#0b1030"
            strokeWidth="44"
            strokeLinecap="round"
          />
          <path
            d="M260 -20C300 120 240 260 300 380C350 480 330 560 360 620"
            fill="none"
            stroke="#0b1030"
            strokeWidth="16"
            strokeLinecap="round"
          />
          <path
            d="M-20 220C220 260 420 180 640 230C860 280 1060 200 1460 250"
            fill="none"
            stroke="#c7d2fe"
            strokeOpacity=".16"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M-20 760C240 720 480 800 760 760C1020 724 1220 780 1460 740"
            fill="none"
            stroke="#c7d2fe"
            strokeOpacity=".16"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M140 -20C180 200 120 420 200 640C250 780 220 860 240 920"
            fill="none"
            stroke="#c7d2fe"
            strokeOpacity=".16"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M1180 -20C1140 180 1220 380 1160 560C1110 720 1180 840 1160 920"
            fill="none"
            stroke="#c7d2fe"
            strokeOpacity=".16"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M-20 420C300 450 520 390 760 420C1000 450 1200 400 1460 430"
            fill="none"
            stroke="#c7d2fe"
            strokeOpacity=".16"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <rect width="1440" height="900" fill="url(#trial-bd-dots)" />
        </svg>
        {pins.map((pin) => (
          <div
            key={`${pin.x}-${pin.y}-${pin.label}`}
            className="FreeTrial__Pin"
            style={{ left: pin.x, top: pin.y, animationDelay: pin.delay }}
          >
            <span className="FreeTrial__Pin__Ring" style={{ backgroundColor: brand, animationDelay: pin.ringDelay }} />
            <span className="FreeTrial__Pin__Dot" style={{ backgroundColor: brand }} />
            <span className="FreeTrial__Pin__Label" style={{ animationDelay: pin.labelDelay }}>{pin.label}</span>
          </div>
        ))}
      </div>

      <div className="FreeTrial__Backdrop__Scene FreeTrial__Backdrop__Scene--mosaic">
        <div
          className={
            'FreeTrial__Mosaic max-md:!tw-left-[-24%] max-md:!tw-right-[-24%] max-md:!tw-top-[-8%] '
            + 'max-md:!tw-h-[165%] max-md:!tw-grid-cols-3 max-md:!tw-auto-rows-[178px] '
            + 'max-md:!tw-gap-2.5'
          }
        >
          <div
            className="FreeTrial__Mosaic__Tile FreeTrial__Mosaic__Tile--crew"
            style={{ background: '#eaeaff' }}
          >
            <div className="FreeTrial__Mosaic__Art FreeTrial__Mosaic__Art--crew">
              <Crew brand={brand} accent={accent} />
            </div>
          </div>
          <div
            className="FreeTrial__Mosaic__Tile FreeTrial__Mosaic__Tile--shop"
            style={{ background: '#eff7fa' }}
          >
            <div className="FreeTrial__Mosaic__Art FreeTrial__Mosaic__Art--shop">
              <Shopfront company={company} brand={brand} accent={accent} />
            </div>
          </div>
          {tiles.map((tile) => (
            <div
              key={tile.id}
              className="FreeTrial__Mosaic__Tile"
              style={{ gridRow: `span ${tile.span}`, background: tile.bg }}
            >
              {tile.img && (
                <img
                  src={tile.img}
                  alt=""
                  referrerPolicy="no-referrer"
                  onError={(event) => {
                    const image = event.currentTarget;
                    const attempt = Number(image.dataset.trialAttempt || '0');
                    const fallback = trialMosaicImageFallback(photos, tile.img, attempt);

                    if (!fallback || fallback === image.src) return;

                    image.dataset.trialAttempt = String(attempt + 1);
                    image.src = fallback;
                  }}
                />
              )}
            </div>
          ))}
        </div>
        <div className="FreeTrial__Mosaic__Veil" />
        <div className="FreeTrial__Mosaic__Scan" />
      </div>

      <div className="FreeTrial__Backdrop__Scene FreeTrial__Backdrop__Scene--phones">
        <svg width="100%" height="100%">
          <defs>
            <pattern id="trial-bd-dots-2" width="22" height="22" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.2" fill="#c7d2fe" fillOpacity=".12" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="#121838" />
          <rect width="100%" height="100%" fill="url(#trial-bd-dots-2)" />
        </svg>
        <div
          className={
            'FreeTrial__Phones max-md:!tw-flex max-md:!tw-items-start max-md:!tw-justify-center '
            + 'max-md:!tw-pt-[108px] max-md:!tw-scale-100'
          }
        >
          <div className="FreeTrial__Phone FreeTrial__Phone--left max-md:tw-hidden">
            <div className="FreeTrial__Phone__Bob">
              <div className="FreeTrial__Phone__Shell">
                <div className="FreeTrial__Phone__Screen">
                  <div className="FreeTrial__Phone__Head" style={{ backgroundColor: brand, color: onColor(brand) }}>
                    <strong>{t('authentication:trial_backdrop_team_chat')}</strong>
                    <span />
                  </div>
                  {chats.map((chat) => (
                    <div key={chat.name + chat.msg} className="FreeTrial__Phone__Row">
                      <em style={{ backgroundColor: chat.color, color: onColor(chat.color) }}>
                        {chat.name.replace(/^All /, '').charAt(0).toUpperCase()}
                      </em>
                      <div>
                        <strong>{chat.name}</strong>
                        <span>{chat.msg}</span>
                      </div>
                      {chat.count > 0 && (
                      <b style={{ backgroundColor: brand, color: onColor(brand) }}>{chat.count}</b>
                    )}
                    </div>
                ))}
                </div>
              </div>
            </div>
          </div>
          <div className="FreeTrial__Phone FreeTrial__Phone--right max-md:!tw-translate-y-0 max-md:!tw-rotate-0">
            <div className="FreeTrial__Phone__Bob FreeTrial__Phone__Bob--late">
              <div className="FreeTrial__Phone__Shell">
                <div className="FreeTrial__Phone__Screen">
                  <div className="FreeTrial__Phone__Head" style={{ backgroundColor: accent, color: onColor(accent) }}>
                    <strong className="max-md:!tw-hidden">{t('authentication:trial_backdrop_academy')}</strong>
                    <strong className="!tw-hidden max-md:!tw-block">
                      {t('authentication:trial_backdrop_welcome', { name: company })}
                    </strong>
                    <small>{t('authentication:trial_backdrop_academy_waiting')}</small>
                  </div>
                  <div className="FreeTrial__Phone__Academy">
                    <div className="FreeTrial__Phone__Course">
                      <div className="FreeTrial__Phone__CourseCover" style={{ backgroundColor: brand }} />
                      <div>
                        <strong>{t('authentication:trial_backdrop_welcome', { name: company })}</strong>
                        <span>{t('authentication:trial_backdrop_lessons')}</span>
                        <i><b style={{ backgroundColor: brand }} /></i>
                      </div>
                    </div>
                    <div className="FreeTrial__Phone__Mini">
                      <span style={{ backgroundColor: accent }} />
                      <div>
                        <strong>{t('authentication:trial_backdrop_course_start')}</strong>
                        <em>{t('authentication:trial_backdrop_not_started')}</em>
                      </div>
                    </div>
                    <div className="FreeTrial__Phone__Mini">
                      <span style={{ backgroundColor: '#19214d' }} />
                      <div>
                        <strong>{t('authentication:trial_backdrop_course_safety')}</strong>
                        <em>{t('authentication:trial_backdrop_completed')}</em>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
